import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import { useParams, Link, useHistory } from 'react-router-dom';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Alert from '@material-ui/lab/Alert';
import ArrowBack from '@material-ui/icons/ArrowBack';
import * as bookingsSelectors from 'selectors/bookings';
import * as actions from 'actions';
import Api from 'api';
import Errors from 'components/shared/Errors';
import Success from 'components/shared/Success';
import Main from 'components/templates/Main';
import CircularProgress from 'components/shared/CircularProgress';
import OverlapWarning from 'components/BookingInfo/OverlapWarning';
import PATHS from 'constants/paths';
import moment from 'logic/moment';
import { setExpirationDateTime } from 'logic/bookingForm';
import { isPayinLocked } from '../helpers/payinLock';
import EditOfferForm from '../components/EditOfferForm';

const BookingEditOffer = () => {
  const { bookingId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const booking = useSelector(bookingsSelectors.singleBooking);
  const isFetching = useSelector(bookingsSelectors.isFetching);
  const errors = useSelector(bookingsSelectors.error);
  const success = useSelector(bookingsSelectors.success);
  const offerStale = useSelector(bookingsSelectors.offerStale);

  // submittedRef guards the redirect effect so a stale `success` from a prior
  // put can't auto-navigate away. Reset on every bookingId change since the
  // route may re-use this instance. The idle dispatch in the same effect
  // clears any stale Success banner that hasn't yet been wiped by
  // notificationSagas' 4s timer (UX-only, not a safety mechanism).
  const submittedRef = React.useRef(false);
  React.useEffect(() => {
    submittedRef.current = false;
    dispatch(actions.putBookingIdle());
    dispatch(actions.fetchBookingRequest({ bookingId }));
  }, [bookingId, dispatch]);

  React.useEffect(() => {
    if (!isFetching && booking && parseInt(booking.id, 10) === parseInt(bookingId, 10)) {
      if (booking.host_change_booking_entry !== 'edit' && booking.conversation_id) {
        history.replace(
          PATHS.conversations.booking
            .replace(':conversationId', booking.conversation_id)
            .replace(':bookingId', bookingId),
        );
      }
    }
  }, [isFetching, booking, bookingId, history]);

  React.useEffect(() => {
    if (success && submittedRef.current && booking?.conversation_id) {
      history.push(
        PATHS.conversations.booking
          .replace(':conversationId', booking.conversation_id)
          .replace(':bookingId', bookingId),
      );
    }
  }, [booking, bookingId, history, success]);

  const [overlap, setOverlap] = React.useState([]);
  const [pendingPayload, setPendingPayload] = React.useState(null);
  const [overlapChecking, setOverlapChecking] = React.useState(false);

  const dispatchPut = React.useCallback(
    (payload) => {
      dispatch(actions.bookingOfferStaleClear());
      submittedRef.current = true;
      dispatch(actions.putBookingRequest({ bookingId, values: payload }));
    },
    [dispatch, bookingId],
  );

  const onSubmit = async ({ check_in, check_out, total_price, booking_expiration_date }) => {
    // Defensive guard against double-submit while the overlap fetch is in
    // flight or the warning is showing. The form's disabled prop already
    // prevents the button from firing in normal use; this protects against
    // programmatic submit and future form refactors.
    if (overlapChecking || overlap.length > 0) return;

    const payload = {
      check_in: moment(check_in).toISOString(),
      check_out: moment(check_out).toISOString(),
      total_price: Math.round(Number(total_price)) * 100,
      booking_expiration_date: setExpirationDateTime(booking_expiration_date, check_in),
      version: booking?.version,
    };

    // Date-only patches need an overlap check. Price/expiration-only patches
    // skip it -- they can't introduce a new overlap.
    const datesChanged =
      !moment(check_in).isSame(moment(booking.check_in), 'minute') ||
      !moment(check_out).isSame(moment(booking.check_out), 'minute');

    if (!datesChanged) {
      dispatchPut(payload);
      return;
    }

    setOverlapChecking(true);
    try {
      const result = booking.cottage_id
        ? await Api.overlapBooking(booking.cottage_id, {
            check_in: payload.check_in,
            check_out: payload.check_out,
          })
        : [];
      const others = (result || []).filter((b) => parseInt(b.id, 10) !== parseInt(bookingId, 10));
      if (others.length === 0) {
        dispatchPut(payload);
        return;
      }
      setOverlap(others);
      setPendingPayload(payload);
    } catch (err) {
      // Overlap is advisory; if the lookup fails, fall through to submit so
      // the host isn't blocked by an unrelated network blip. Backend still
      // gates paid-booking overlaps with a 400.
      dispatchPut(payload);
    } finally {
      setOverlapChecking(false);
    }
  };

  const onOverlapAccept = () => {
    if (pendingPayload) dispatchPut(pendingPayload);
    setOverlap([]);
    setPendingPayload(null);
  };

  const onOverlapCancel = () => {
    setOverlap([]);
    setPendingPayload(null);
  };

  const showOverlap = overlap.length > 0;

  const isCurrentBooking =
    booking?.id != null && parseInt(booking.id, 10) === parseInt(bookingId, 10);
  const backPath =
    isCurrentBooking &&
    booking.conversation_id &&
    PATHS.conversations.booking
      .replace(':conversationId', booking.conversation_id)
      .replace(':bookingId', bookingId);

  return (
    <Main
      title={
        <Button color="inherit" component={Link} to={backPath || PATHS.conversations.root}>
          <ArrowBack /> &nbsp;{' '}
          <FormattedMessage id="editOfferBooking.heading" defaultMessage="Edit booking offer" /> #
          {booking?.id}
        </Button>
      }
    >
      {isFetching && <CircularProgress />}
      {!isFetching &&
        booking &&
        parseInt(booking.id, 10) === parseInt(bookingId, 10) &&
        booking.host_change_booking_entry === 'edit' && (
          <>
            {isPayinLocked(booking) ? (
              <Box m={2}>
                <Alert severity="info" style={{ display: 'inline-flex' }}>
                  <FormattedMessage
                    id="editOfferBooking.payinLocked"
                    defaultMessage="A guest is paying right now. Try again in a few minutes or once the guest is done."
                  />
                </Alert>
              </Box>
            ) : (
              offerStale && (
                <Box m={2}>
                  <Alert severity="warning" style={{ display: 'inline-flex' }}>
                    <FormattedMessage
                      id="editOfferBooking.bookingChanged"
                      defaultMessage="The booking has changed since you opened this form. The fields have been refreshed with the latest details — please review and resave if you still want to update."
                    />
                  </Alert>
                </Box>
              )
            )}
            {showOverlap && (
              <Box m={2}>
                <OverlapWarning
                  overlap={overlap}
                  onAccept={onOverlapAccept}
                  onCancel={onOverlapCancel}
                />
              </Box>
            )}
            <EditOfferForm
              booking={booking}
              onSubmit={onSubmit}
              disabled={isPayinLocked(booking) || showOverlap || overlapChecking}
            />
          </>
        )}

      <Success success={success} />
      <Errors errors={errors} />
    </Main>
  );
};

export default BookingEditOffer;
